<template>
  <div class="video-page">
    <!-- 页面头部 -->
    <div class="page-header page-header-4">
      <div class="container">
        <h1 class="page-title">{{ pageTitle }}</h1>
        <div class="header-divider"></div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="page-content">
      <div class="container">
        <!-- 加载状态 -->
        <div v-if="isLoading" class="loading-container">
          <div class="loading-spinner"></div>
          <p class="loading-text">{{ t('common.loading') }}</p>
        </div>

        <!-- 错误状态 -->
        <div v-else-if="errorMessage" class="error-container">
          <div class="error-icon">
            <el-icon size="60"><Warning /></el-icon>
          </div>
          <h3 class="error-title">{{ t('common.error') }}</h3>
          <p class="error-message">{{ errorMessage }}</p>
          <button class="retry-button" @click="fetchVideoData">
            {{ t('common.retry') }}
          </button>
        </div>

        <!-- 内容区域 -->
        <div v-else class="content-wrapper">
          <!-- 视频播放区域 -->
          <div v-if="videosList.length" class="video-player-section">
            <div class="video-layout">
              <!-- 左侧：视频播放器 -->
              <div class="video-main">
                <div class="video-wrapper">
                  <video
                    ref="videoPlayer"
                    class="company-video"
                    :src="currentVideoPath"
                    controls
                    autoplay
                    playsinline
                    preload="metadata"
                    @loadeddata="onVideoLoaded"
                    @error="onVideoError"
                    @ended="onVideoEnded"
                  >
                    <source :src="currentVideoPath" type="video/mp4">
                    {{ t('video.browser_not_support') }}
                  </video>

                  <!-- 视频加载状态 -->
                  <div v-if="isVideoLoading" class="video-loading">
                    <div class="loading-spinner"></div>
                    <p class="loading-text">{{ t('video.buffering') }}</p>
                  </div>
                </div>

                <!-- 视频信息 -->
                <!-- <div class="video-info">
                  <h3 class="video-title">{{ currentVideoTitle }}</h3>
                  <div class="video-meta">
                    <div class="meta-item">
                      <el-icon><Clock /></el-icon>
                      <span>{{ videoDuration }}</span>
                    </div>
                    <div class="meta-item">
                      <el-icon><Calendar /></el-icon>
                      <span>{{ currentVideoDate }}</span>
                    </div>
                  </div>
                </div> -->

                <!-- 移动端：视频清单按钮 -->
                <div v-if="videosList.length > 1" class="mobile-playlist-btn-wrapper">
                  <button class="mobile-playlist-btn" @click="showPlaylistDrawer = true">
                    <el-icon><List /></el-icon>
                    <span>{{ t('video.playlist') }}</span>
                  </button>
                </div>
              </div>

              <!-- 右侧：视频列表（桌面端） -->
              <div v-if="videosList.length > 1" class="video-sidebar">
                <div class="sidebar-header">
                  <h4 class="sidebar-title">{{ t('video.playlist') }}</h4>
                  <span class="sidebar-count">{{ videosList.length }}{{ t('video.videos_count') }}</span>
                </div>
                <div class="sidebar-list">
                  <div
                    v-for="(video, index) in videosList"
                    :key="index"
                    class="sidebar-item"
                    :class="{ active: activeVideoIndex === index }"
                    @click="switchVideo(index)"
                  >
                    <div class="sidebar-item-index">{{ index + 1 }}</div>
                    <div class="sidebar-item-info">
                      <p class="sidebar-item-title">{{ video.video_name || `${t('video.video_title')}${index + 1}` }}</p>
                      <p v-if="video.video_date" class="sidebar-item-date">{{ video.video_date }}</p>
                    </div>
                    <el-icon v-if="activeVideoIndex === index" class="sidebar-item-playing"><VideoPlay /></el-icon>
                  </div>
                </div>
              </div>
            </div>

            <!-- 富文本内容（如果有） -->
            <div
              v-if="richtextHtml && richtextHtml.trim()"
              class="richtext-content"
              v-html="richtextHtml"
            ></div>
          </div>

          <!-- 空状态（当没有视频时显示） -->
          <div v-else class="empty-state">
            <div class="empty-icon">
              <el-icon size="80" color="var(--el-color-primary-light-8)">
                <VideoCamera />
              </el-icon>
            </div>
            <h3 class="empty-title">{{ t('common.no_data') }}</h3>
            <p class="empty-description">{{ t('video.no_video') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 移动端底部抽屉：视频清单 -->
    <el-drawer
      v-model="showPlaylistDrawer"
      :title="t('video.playlist')"
      direction="btt"
      size="70%"
      class="playlist-drawer"
      :with-header="true"
    >
      <div class="drawer-playlist">
        <div
          v-for="(video, index) in videosList"
          :key="index"
          class="drawer-item"
          :class="{ active: activeVideoIndex === index }"
          @click="switchVideo(index); showPlaylistDrawer = false"
        >
          <div class="drawer-item-index">{{ index + 1 }}</div>
          <div class="drawer-item-info">
            <p class="drawer-item-title">{{ video.video_name || `${t('video.video_title')}${index + 1}` }}</p>
            <p v-if="video.video_date" class="drawer-item-date">{{ video.video_date }}</p>
          </div>
          <el-icon v-if="activeVideoIndex === index" class="drawer-item-playing"><VideoPlay /></el-icon>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  Warning,
  VideoPlay,
  VideoCamera,
  Clock,
  Calendar,
  List
} from '@element-plus/icons-vue'
import { getSinglePageContent } from "@/assets/api/website";

// 使用 i18n
const { locale, t } = useI18n()

// 响应式数据
const pageData = ref(null)
const isLoading = ref(false)
const errorMessage = ref('')
const videoPlayer = ref(null)

// 视频切换相关
const activeVideoIndex = ref(0) // 当前激活的视频索引
const videosList = ref([]) // 视频列表

// 视频状态
const isVideoLoading = ref(true)
const videoDuration = ref('00:00')
const currentVideoTitle = ref('')
const currentVideoDate = ref('')

// 移动端抽屉
const showPlaylistDrawer = ref(false)

// 计算属性
const pageTitle = computed(() => {
  return pageData.value?.page_name || t('menu.company_video')
})

const richtextHtml = computed(() => {
  return pageData.value?.content?.richtext || ''
})

// 当前播放的视频路径
const currentVideoPath = computed(() => {
  return videosList.value[activeVideoIndex.value]?.video_url || ''
})

// 获取视频列表
const getVideosList = computed(() => {
  // 兼容新旧格式：优先取videos数组，没有则兼容旧的video_url
  const content = pageData.value?.content || {}
  if (Array.isArray(content.videos) && content.videos.length) {
    return content.videos.filter(video => video?.video_url) // 过滤掉无URL的视频
  } else if (content.video_url) {
    // 兼容旧格式
    return [{
      video_url: content.video_url,
      video_name: content.video_title || t('video.company_video'),
      video_date: content.video_date || ''
    }]
  }
  return []
})

// 切换视频方法
const switchVideo = (index) => {
  if (index === activeVideoIndex.value) return // 点击当前激活的视频，不处理

  // 暂停当前视频
  if (videoPlayer.value) {
    videoPlayer.value.pause()
  }

  // 更新激活索引
  activeVideoIndex.value = index

  // 重置视频状态
  isVideoLoading.value = true
  videoDuration.value = '00:00'

  // 设置当前视频信息
  const currentVideo = videosList.value[index]
  currentVideoTitle.value = currentVideo.video_name || `视频${index + 1}`
  currentVideoDate.value = currentVideo.video_date || new Date().toLocaleDateString()

  // 等待DOM更新后加载新视频
  nextTick(() => {
    if (videoPlayer.value) {
      // 重新加载视频
      videoPlayer.value.load()
      // 自动播放新视频
      videoPlayer.value.play().catch(err => {
        console.log('自动播放失败:', err)
      })
      
      // 向上平滑滚动至视频完整出现在视窗内（留出100px的顶部间距）
      const rect = videoPlayer.value.getBoundingClientRect()
      const scrollTop = window.pageYOffset + rect.top - 100
      window.scrollTo({
        top: scrollTop,
        behavior: 'smooth'
      })
    }
  })
}

// 视频事件处理
const onVideoLoaded = () => {
  isVideoLoading.value = false

  // 设置视频时长
  const duration = videoPlayer.value?.duration || 0
  const minutes = Math.floor(duration / 60)
  const seconds = Math.floor(duration % 60)
  videoDuration.value = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`

  // 设置当前视频标题和日期（如果还没设置）
  if (!currentVideoTitle.value) {
    const currentVideo = videosList.value[activeVideoIndex.value]
    currentVideoTitle.value = currentVideo?.video_name || t('video.company_video')
    currentVideoDate.value = currentVideo?.video_date || new Date().toLocaleDateString()
  }
}

const onVideoError = (event) => {
  console.error('视频加载失败:', event)
  errorMessage.value = t('video.load_failed')
  isVideoLoading.value = false
}

const onVideoEnded = () => {
}

// 获取页面数据
const fetchVideoData = async () => {
  isLoading.value = true
  errorMessage.value = ''

  try {
    // 使用当前的语言
    const currentLocale = locale.value

    const response = await getSinglePageContent({
      lang: currentLocale,
      path: '/company-video'
    })

    if (response && response.content) {
      pageData.value = response
      // 更新视频列表
      videosList.value = getVideosList.value
      // 重置激活索引
      activeVideoIndex.value = 0

      // 设置初始视频信息
      const firstVideo = videosList.value[0]
      if (firstVideo) {
        currentVideoTitle.value = firstVideo.video_name || t('video.company_video')
        currentVideoDate.value = firstVideo.video_date || new Date().toLocaleDateString()
      }
    } else {
      pageData.value = null
      videosList.value = []
    }
  } catch (error) {
    console.error('获取视频数据失败:', error)
    errorMessage.value = t('common.load_failed')
    videosList.value = []
  } finally {
    isLoading.value = false
  }
}

// 组件挂载时获取数据
onMounted(() => {
  fetchVideoData()
})

// 组件卸载时清理
onUnmounted(() => {
  if (videoPlayer.value) {
    videoPlayer.value.pause()
    videoPlayer.value = null
  }
})

// 监听语言变化，重新加载数据
watch(locale, () => {
  fetchVideoData()
})



// 监听视频列表变化
watch(getVideosList, (newList) => {
  videosList.value = newList
  // 如果当前激活索引超出范围，重置为0
  if (activeVideoIndex.value >= newList.length && newList.length > 0) {
    activeVideoIndex.value = 0
  }
}, { immediate: true, deep: true })
</script>

<style scoped lang="scss">
// 导入全局样式变量
@import '@/assets/css/common';

.video-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #f9fbf7 0%, #ffffff 100%);

  @media (max-width: 992px) {
    min-height: auto;
  }
}
// 页面头部样式
.page-header {
  padding: 200px 0 100px;
  /* 背景核心优化：居中 + 覆盖 + 固定（可选） + 轻微模糊 */
  background: url('@/assets/img/header.jpg') center center / cover no-repeat;
  /* 高斯模糊：使用伪元素实现（避免模糊影响内容），模糊值 2px（极轻微） */
  position: relative;
  border-bottom: 1px solid rgba($primary-color, 0.1);

  /* 伪元素实现背景模糊（不影响文字/内容） */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: inherit; /* 继承父元素背景图 */
    filter: blur(4px); /* 极轻微高斯模糊，可调整（1-3px 都很淡） */
    z-index: 1; /* 放在内容下方 */
    transform: scale(1.01); /* 消除模糊边缘白边 */
  }

  /* 确保内容层级高于模糊层 */
  & > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 768px) {
    padding: 140px 0 60px;
  }

  @media (max-width: 480px) {
    padding: 140px 0 60px;
  }
}

.page-title {
  font-family: $font-serif;
  font-size: 2.5rem;
  font-weight: 700;
  color: white;
  text-align: center;
  margin-bottom: 20px;
  letter-spacing: 2px;
  /* 浅白色文字阴影：极淡、低模糊、小偏移，仅增强文字层次感 */
  text-shadow: 0 1px 2px $primary-color;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.75rem;
    margin-bottom: 16px;
    /* 移动端阴影稍淡，避免占视觉焦点 */
    text-shadow: 0 1px 1px rgba(255, 255, 255, 0.15);
  }
}

.header-divider {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, $primary-color, $secondary-color);
  margin: 0 auto;
  border-radius: 2px;

  @media (max-width: 480px) {
    width: 60px;
    height: 3px;
  }
}
.header-divider {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, $primary-color, $secondary-color);
  margin: 0 auto 20px;
  border-radius: 2px;

  @media (max-width: 480px) {
    width: 60px;
    height: 3px;
    margin-bottom: 16px;
  }
}

.page-subtitle {
  font-size: 1.125rem;
  color: $text-light;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0 20px;
  }

  @media (max-width: 480px) {
    font-size: 0.9375rem;
    padding: 0 16px;
  }
}

// 主要内容区域
.page-content {
  padding: 60px 0;

  @media (max-width: 768px) {
    padding: 40px 0;
  }

  @media (max-width: 480px) {
    padding: 30px 0;
  }
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 60px;
}

// 视频播放器区域
.video-player-section {
  background: $white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: $shadow-light;
  border: 1px solid rgba($primary-color, 0.1);
}

// 仿B站左右布局
.video-layout {
  display: flex;
  gap: 0;
  min-height: 500px;
}

.video-main {
  flex: 1;
  min-width: 0;
}

.video-sidebar {
  width: 320px;
  flex-shrink: 0;
  border-left: 1px solid rgba($primary-color, 0.1);
  background: #fafafa;
  display: flex;
  flex-direction: column;
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid rgba($primary-color, 0.1);
}

.sidebar-title {
  font-size: 1rem;
  font-weight: 600;
  color: $primary-color-2;
  margin: 0;
}

.sidebar-count {
  font-size: 0.875rem;
  color: $text-light;
}

.sidebar-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}

.sidebar-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba($primary-color, 0.05);

    .sidebar-item-title {
      color: $primary-color;
    }
  }

  &.active {
    background-color: rgba($primary-color, 0.08);

    .sidebar-item-index {
      background: $primary-color;
      color: $white;
    }

    .sidebar-item-title {
      color: $primary-color;
      font-weight: 600;
    }
  }
}

.sidebar-item-index {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background: rgba($primary-color, 0.1);
  color: $primary-color;
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.sidebar-item-info {
  flex: 1;
  min-width: 0;
}

.sidebar-item-title {
  font-size: 0.875rem;
  color: $text-color;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: 0;
  transition: color 0.2s ease;
}

.sidebar-item-date {
  font-size: 0.75rem;
  color: $text-light;
  margin: 4px 0 0;
}

.sidebar-item-playing {
  color: $primary-color;
  font-size: 1rem;
  flex-shrink: 0;
}

// 移动端视频清单按钮
.mobile-playlist-btn-wrapper {
  display: none;
  padding: 20px;
}

.mobile-playlist-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px;
  background: rgba($primary-color, 0.05);
  border: 1px solid rgba($primary-color, 0.15);
  border-radius: 8px;
  color: $primary-color;
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba($primary-color, 0.1);
  }

  .el-icon {
    font-size: 1.125rem;
  }
}

// 响应式：平板及以下改为上下布局
@media (max-width: 992px) {
  .video-layout {
    flex-direction: column;
    min-height: auto;
  }

  .video-sidebar {
    display: none;
  }

  .mobile-playlist-btn-wrapper {
    display: block;
  }
}

// 视频包装器
.video-wrapper {
  position: relative;
  width: 100%;
  background: #000;
  aspect-ratio: 16/9;
  overflow: hidden;
}

.company-video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  background: #000;
}

// 视频加载状态
.video-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.8);
  z-index: 10;

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba($white, 0.1);
    border-top-color: $white;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }

  .loading-text {
    color: $white;
    font-size: 1rem;
  }
}

// 视频信息
.video-info {
  padding: 30px;

  @media (max-width: 768px) {
    padding: 24px;
  }

  @media (max-width: 480px) {
    padding: 20px;
  }
}

.video-title {
  font-family: $font-serif;
  font-size: 1.5rem;
  font-weight: 600;
  color: $primary-color-2;
  margin-bottom: 16px;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 1.375rem;
  }

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
}

.video-meta {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    gap: 16px;
  }
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: $text-light;
  font-size: 0.9375rem;

  .el-icon {
    color: $primary-color;
    font-size: 1.125rem;
  }
}

// 富文本内容样式
:deep(.richtext-content) {
  padding: 0 30px 30px;
  line-height: 1.8;

  // 基础段落样式
  p {
    //margin-bottom: 1.5em;
    color: $gray-80;
    font-size: 1.125rem;
    text-align: justify;
    text-indent: 2em;

    &:last-child {
      margin-bottom: 0;
    }
  }

  // 响应式调整
  @media (max-width: 768px) {
    padding: 0 24px 24px;

    p {
      font-size: 1rem;
    }
  }

  @media (max-width: 480px) {
    padding: 0 20px 20px;

    p {
      font-size: 0.9375rem;
    }
  }
}

// 加载状态
.loading-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 3px solid rgba($primary-color, 0.1);
  border-top-color: $primary-color;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

.loading-text {
  font-size: 1rem;
  color: $text-light;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

// 错误状态
.error-container {
  text-align: center;
  padding: 60px 20px;

  @media (max-width: 768px) {
    padding: 40px 20px;
  }
}

.error-icon {
  margin-bottom: 20px;
  color: #f56c6c;
}

.error-title {
  font-size: 1.5rem;
  color: #f56c6c;
  margin-bottom: 12px;
  font-weight: 600;

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
}

.error-message {
  font-size: 1rem;
  color: $text-light;
  margin-bottom: 20px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.retry-button {
  background: $primary-color;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: $primary-light-3;
    transform: translateY(-2px);
  }
}

// 空状态
.empty-state {
  text-align: center;
  padding: 80px 20px;
  background: $white;
  border-radius: 16px;
  box-shadow: $shadow-light;
  border: 1px solid rgba($primary-color, 0.1);

  @media (max-width: 768px) {
    padding: 60px 20px;
  }

  @media (max-width: 480px) {
    padding: 40px 16px;
  }
}

.empty-icon {
  margin-bottom: 24px;
  opacity: 0.7;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
}

.empty-title {
  font-size: 1.5rem;
  color: $text-color;
  margin-bottom: 12px;
  font-weight: 600;

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
}

.empty-description {
  font-size: 1rem;
  color: $text-light;
  max-width: 400px;
  margin: 0 auto;
  line-height: 1.5;

  @media (max-width: 480px) {
    font-size: 0.9375rem;
  }
}

// 移动端抽屉内视频列表
.playlist-drawer {
  :deep(.el-drawer__header) {
    margin-bottom: 0;
    padding: 16px 20px;
    border-bottom: 1px solid rgba($primary-color, 0.1);

    .el-drawer__title {
      font-size: 1rem;
      font-weight: 600;
      color: $primary-color-2;
    }
  }

  :deep(.el-drawer__body) {
    padding: 0;
  }
}

.drawer-playlist {
  padding: 8px 0;
}

.drawer-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: rgba($primary-color, 0.05);

    .drawer-item-title {
      color: $primary-color;
    }
  }

  &.active {
    background-color: rgba($primary-color, 0.08);

    .drawer-item-index {
      background: $primary-color;
      color: $white;
    }

    .drawer-item-title {
      color: $primary-color;
      font-weight: 600;
    }
  }
}

.drawer-item-index {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: rgba($primary-color, 0.1);
  color: $primary-color;
  font-size: 0.875rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.drawer-item-info {
  flex: 1;
  min-width: 0;
}

.drawer-item-title {
  font-size: 0.9375rem;
  color: $text-color;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: 0;
  transition: color 0.2s ease;
}

.drawer-item-date {
  font-size: 0.8125rem;
  color: $text-light;
  margin: 4px 0 0;
}

.drawer-item-playing {
  color: $primary-color;
  font-size: 1.125rem;
  flex-shrink: 0;
}
</style>